import axiosInstance from '../../config/axios.config';

/**
 * Lending Network Admin Service
 *
 * Handles API calls for:
 * - Tenant admin lending network management (approve/deny/remove + settings)
 * - Company admin private lending network management
 */
const lendingNetworkService = {

    // =========================================================================
    // TENANT ADMIN — LENDING NETWORK SETTINGS
    // =========================================================================

    getSettings: async () => {
        try {
            const response = await axiosInstance.get('/admin/lending-network/settings');
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to fetch settings' };
        }
    },

    updateSettings: async (approvalMode) => {
        try {
            const response = await axiosInstance.put('/admin/lending-network/settings', {
                approval_mode: approvalMode,
            });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to update settings' };
        }
    },

    // =========================================================================
    // TENANT ADMIN — VISIBILITY CEILING
    // =========================================================================

    /**
     * The tenant-wide ceiling on how far member profiles and posts travel.
     * Returns { profile_visibility, post_visibility, options[] }.
     */
    getVisibility: async () => {
        try {
            const response = await axiosInstance.get('/admin/lending-network/visibility');
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to fetch visibility settings' };
        }
    },

    /**
     * @param {'public'|'tenant'} profileVisibility
     * @param {'public'|'tenant'} postVisibility
     */
    updateVisibility: async (profileVisibility, postVisibility) => {
        try {
            const response = await axiosInstance.put('/admin/lending-network/visibility', {
                profile_visibility: profileVisibility,
                post_visibility: postVisibility,
            });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to update visibility settings' };
        }
    },

    // =========================================================================
    // TENANT ADMIN — MEMBER MANAGEMENT
    // =========================================================================

    /**
     * List all lending network profiles for this tenant.
     * @param {Object} params
     * @param {'pending'|'approved'|'denied'|'all'} params.status
     * @param {string} params.search
     * @param {number} params.page
     * @param {number} params.limit
     */
    getMembers: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/admin/lending-network/members', { params });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to fetch members', data: { members: [], pagination: { total: 0, current_page: 1 } } };
        }
    },

    approveMember: async (profileId) => {
        try {
            const response = await axiosInstance.put(`/admin/lending-network/${profileId}/approve`);
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to approve member' };
        }
    },

    denyMember: async (profileId, reason = '') => {
        try {
            const response = await axiosInstance.put(`/admin/lending-network/${profileId}/deny`, { reason });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to deny member' };
        }
    },

    removeMember: async (profileId) => {
        try {
            const response = await axiosInstance.delete(`/admin/lending-network/${profileId}`);
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to remove member' };
        }
    },

    // =========================================================================
    // COMPANY ADMIN — PRIVATE LENDING NETWORK
    // =========================================================================

    getCompanySettings: async () => {
        try {
            const response = await axiosInstance.get('/company/lending-network/settings');
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to fetch settings' };
        }
    },

    updateCompanySettings: async (approvalMode) => {
        try {
            const response = await axiosInstance.put('/company/lending-network/settings', {
                approval_mode: approvalMode,
            });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to update settings' };
        }
    },

    getCompanyMembers: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/company/lending-network/members', { params });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to fetch company members', data: { members: [], pagination: { total: 0, current_page: 1 } } };
        }
    },

    approveCompanyMember: async (profileId) => {
        try {
            const response = await axiosInstance.put(`/company/lending-network/${profileId}/approve`);
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to approve member' };
        }
    },

    denyCompanyMember: async (profileId, reason = '') => {
        try {
            const response = await axiosInstance.put(`/company/lending-network/${profileId}/deny`, { reason });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to deny member' };
        }
    },

    removeCompanyMember: async (profileId) => {
        try {
            const response = await axiosInstance.delete(`/company/lending-network/${profileId}`);
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to remove member' };
        }
    },

    // =========================================================================
    // COMPANY ADMIN — VISIBILITY POLICY
    // =========================================================================

    /**
     * The company's visibility policy for its employees and agents, plus the
     * tenant ceiling above it and the resulting effective levels — the UI shows
     * all three so a capped setting doesn't look broken.
     */
    getCompanyVisibility: async () => {
        try {
            const response = await axiosInstance.get('/company/lending-network/visibility');
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to fetch visibility settings' };
        }
    },

    /**
     * @param {Object} policy
     * @param {boolean} policy.profiles_enabled
     * @param {'public'|'tenant'|'company'} policy.profile_visibility
     * @param {'public'|'tenant'|'company'} policy.post_visibility
     * @param {boolean} policy.post_approval_required
     */
    updateCompanyVisibility: async (policy) => {
        try {
            const response = await axiosInstance.put('/company/lending-network/visibility', policy);
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to update visibility settings' };
        }
    },

    /**
     * Show or hide one member's profile outside the company.
     * @param {boolean} visible false → that member is company-only
     */
    updateCompanyMemberVisibility: async (profileId, visible) => {
        try {
            const response = await axiosInstance.put(`/company/lending-network/${profileId}/visibility`, { visible });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to update member visibility' };
        }
    },

    // =========================================================================
    // COMPANY ADMIN — POST APPROVAL QUEUE
    // =========================================================================

    /**
     * Posts written by this company's employees and agents.
     * @param {Object} params
     * @param {'pending'|'approved'|'rejected'|'all'} params.status
     * @param {number} params.page
     * @param {number} params.limit
     */
    getCompanyPosts: async (params = {}) => {
        try {
            const response = await axiosInstance.get('/company/lending-network/posts', { params });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error?.response?.data?.message || 'Failed to fetch posts',
                data: { posts: [], pagination: { total: 0, current_page: 1 }, counts: { pending: 0, approved: 0, rejected: 0 } },
            };
        }
    },

    approveCompanyPost: async (postId) => {
        try {
            const response = await axiosInstance.put(`/company/lending-network/posts/${postId}/approve`);
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to approve post' };
        }
    },

    rejectCompanyPost: async (postId, reason = '') => {
        try {
            const response = await axiosInstance.put(`/company/lending-network/posts/${postId}/reject`, { reason });
            return response.data;
        } catch (error) {
            return { success: false, message: error?.response?.data?.message || 'Failed to reject post' };
        }
    },

};

export default lendingNetworkService;
